/**
 * useEWSConfig
 *
 * Loads the view -> indicator mapping + dropdown labels for the EWS dashboard.
 *
 * Calls GET /v1/ews/config[?batch_id=<id>] on mount. The browser HTTP cache
 * (Cache-Control: private, max-age=300, stale-while-revalidate=3600 + ETag,
 * all set FAS-side) transparently handles reuse, revalidation, and per-URL
 * cache keys (tenant vs batch-scoped). No client-side cache logic lives here.
 *
 * Resolution model (FAS-side, see EWSConfigService):
 *   - With batchId  → 3-level FK lookup (batch > tenant > global default).
 *   - Without batch → tenant-active with global fallback.
 *
 * NO HARDCODED FALLBACK: surfaces `error` + `retry()` on failure.
 *
 * Usage:
 *   const { config, loading, error, retry } = useEWSConfig(batchId);
 *   if (loading) return <Spinner/>;
 *   if (error || !config) return <ErrorBanner onRetry={retry} message={...}/>;
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ewsApiService, EWSConfigResponse } from '../services/ews-api.service';

interface UseEWSConfigResult {
  config: EWSConfigResponse | null;
  loading: boolean;
  error: Error | null;
  retry: () => void;
}

export const useEWSConfig = (batchId?: string): UseEWSConfigResult => {
  const [config, setConfig] = useState<EWSConfigResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Cancel stale fetches when batchId changes or retry() is called mid-flight.
  const inFlightRef = useRef(0);

  const fetchConfig = useCallback(async (scopeBatchId: string | undefined) => {
    const requestId = ++inFlightRef.current;
    setLoading(true);
    setError(null);
    try {
      const fetched = await ewsApiService.getConfig(undefined, scopeBatchId);
      if (inFlightRef.current !== requestId) return;
      if (!fetched.views || fetched.views.length === 0) {
        throw new Error('EWS config returned no views — ews_view_config table is empty or filtered out by tenant/batch');
      }
      setConfig(fetched);
      setLoading(false);
    } catch (err) {
      if (inFlightRef.current !== requestId) return;
      setError(err instanceof Error ? err : new Error(String(err)));
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig(batchId);
    return () => {
      inFlightRef.current = -1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  const retry = useCallback(() => fetchConfig(batchId), [fetchConfig, batchId]);

  return { config, loading, error, retry };
};
